import { ColumnDef } from '@tanstack/react-table';
import { Trophy, Medal } from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { DataTable } from '@/shared/components/DataTable/DataTable';
import { useTeamLeaderboardAdapter, LeaderboardRow } from './hooks/useAdminCharts';

export const adminTeamLeaderboardConfig: WidgetConfig = {
  id: 'admin-team-leaderboard',
  title: 'Team Leaderboard',
  icon: 'workspace_premium',
  category: 'list',
  description: 'Employee Performance Ranking',
  refreshStrategy: 'interval',
  defaultSize: { w: 12, h: 4 },
  priority: 4,
  supportedRoles: ['admin', 'superadmin'],
};

// Columns mirror the legacy `emp-stats-tbody` table (public/index.html headers,
// public/app.js cell rendering). Sorting is disabled to preserve the backend's
// leads-ranking order, matching production.
const columns: ColumnDef<LeaderboardRow>[] = [
  {
    accessorKey: 'rank',
    header: 'Rank',
    enableSorting: false,
    cell: ({ row }) => {
      const { rank } = row.original;
      if (rank === 1) {
        return <Trophy className="h-[18px] w-[18px] text-warning" />;
      }
      if (rank === 2) {
        return <Medal className="h-[18px] w-[18px] text-on-surface-variant" />;
      }
      return <span className="text-xs text-on-surface-variant">{rank}</span>;
    },
  },
  {
    accessorKey: 'displayName',
    header: 'Member',
    enableSorting: false,
    cell: ({ row }) => {
      const e = row.original;
      return (
        <div className="flex items-center gap-sm">
          <div className="w-6 h-6 rounded-full bg-surface-container-highest border border-outline flex items-center justify-center font-bold text-[10px] text-on-surface">
            {e.avatarInitials}
          </div>
          <div>
            <div className="font-body-sm text-body-sm font-semibold text-on-surface leading-tight flex items-center gap-1">
              {e.displayName}
              {e.role === 'admin' && (
                <span className="px-1 py-px rounded bg-primary/10 border border-primary/20 text-primary text-[9px] font-bold">
                  ADMIN
                </span>
              )}
              {!e.active && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-danger/10 border border-danger/20 text-danger text-[9px] uppercase font-bold">
                  disabled
                </span>
              )}
            </div>
            <div className="font-data-md text-[10px] text-on-surface-variant">@{e.username}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'totalLeads',
    header: 'Total Leads',
    enableSorting: false,
    cell: ({ row }) => {
      const e = row.original;
      return (
        <div className="flex items-center justify-end gap-sm font-data-md text-data-md text-on-surface">
          <span>{e.totalLeads}</span>
          <div className="w-[40px] h-[3px] bg-surface-container-highest rounded-full overflow-hidden">
            <div className="bg-primary h-full" style={{ width: `${e.barPct}%` }} />
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'leadsToday',
    header: 'Today',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right font-data-md text-data-md text-on-surface-variant">
        {row.original.leadsToday}
      </div>
    ),
  },
  {
    accessorKey: 'leadsWeek',
    header: 'This Week',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right font-data-md text-data-md text-on-surface-variant">
        {row.original.leadsWeek}
      </div>
    ),
  },
  {
    accessorKey: 'contacted',
    header: 'Contacted',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right font-data-md text-data-md text-on-surface-variant">
        {row.original.contacted}
      </div>
    ),
  },
  {
    accessorKey: 'converted',
    header: 'Converted',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right font-data-md text-data-md font-semibold text-success">
        {row.original.converted}
      </div>
    ),
  },
  {
    accessorKey: 'totalJobs',
    header: 'Scans',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right font-data-md text-data-md text-on-surface-variant">
        {row.original.totalJobs}
      </div>
    ),
  },
  {
    accessorKey: 'lastActiveLabel',
    header: 'Last Active',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right font-data-md text-[11px] text-on-surface-variant">
        {row.original.lastActiveLabel}
      </div>
    ),
  },
];

export function AdminTeamLeaderboardWidget() {
  const { rows, isLoading, isError, error, refetch } = useTeamLeaderboardAdapter();

  return (
    <WidgetContainer
      config={adminTeamLeaderboardConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => {
        refetch();
      }}
    >
      {rows.length === 0 ? (
        <p className="text-on-surface-variant text-body-sm italic text-center py-6">
          No employees yet. Add one from the Team tab.
        </p>
      ) : (
        <DataTable columns={columns} data={rows} searchable={false} />
      )}
    </WidgetContainer>
  );
}
